import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Keyboard as KeyboardIcon,
} from "@mui/icons-material";
import { FormikProvider } from "formik";
import { AddButton } from "../AddButton";

export interface BaseFormPageProps {
  // Header props
  title: string;
  subtitle?: string;
  onBack: () => void;

  // Form props
  formik: any; // Will be typed more specifically in implementations
  children: React.ReactNode;

  // Loading and state props
  isLoading?: boolean;
  isSubmitting?: boolean;

  // Message props
  successMessage?: string;
  errorMessage?: string;

  // Form completion
  completionPercentage?: number;
  showValidationErrors?: boolean;

  // Action props
  submitButtonText?: string;
  showKeyboardShortcuts?: boolean;
  showUnsavedChangesWarning?: boolean;
  submitButtonSx?: any; // Custom styling for submit button

  // Layout props
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

const BaseFormPage: React.FC<BaseFormPageProps> = ({
  title,
  subtitle,
  onBack,
  formik,
  children,
  isLoading = false,
  isSubmitting = false,
  successMessage,
  errorMessage,
  completionPercentage,
  showValidationErrors = false,
  submitButtonText = "Save Changes",
  showKeyboardShortcuts = true,
  showUnsavedChangesWarning = true,
  submitButtonSx,
  maxWidth = "xl",
}) => {
  const {
    dirty,
    errors,
    handleSubmit,
    isValid,
  } = formik;
  const hasFormErrors = Object.keys(errors).length > 0;
  const [shortcutsAnchorEl, setShortcutsAnchorEl] =
    useState<HTMLElement | null>(null);
  const isShortcutsPopoverOpen = Boolean(shortcutsAnchorEl);

  const handleOpenShortcuts = (event: React.MouseEvent<HTMLElement>) => {
    setShortcutsAnchorEl(event.currentTarget);
  };

  const handleCloseShortcuts = () => {
    setShortcutsAnchorEl(null);
  };

  const handleBack = useCallback(() => {
    if (dirty) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?",
      );
      if (!confirmLeave) return;
    }
    onBack();
  }, [dirty, onBack]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!showKeyboardShortcuts) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((event.ctrlKey || event.metaKey) && event.key === "s") {
        event.preventDefault();
        if (!isSubmitting && isValid) {
          handleSubmit();
        }
      }
      // Escape to go back (with confirmation if dirty)
      if (event.key === "Escape") {
        event.preventDefault();
        handleBack();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isSubmitting,
    isValid,
    handleSubmit,
    handleBack,
    showKeyboardShortcuts,
  ]);

  const renderFormErrors = () => {
    if (!showValidationErrors || !hasFormErrors) return null;

    const errorItems: React.ReactNode[] = [];
    let keyIndex = 0;

    const processErrors = (errors: any, prefix = "") => {
      Object.entries(errors).forEach(([field, error]) => {
        const fullField = prefix ? `${prefix}.${field}` : field;

        if (
          typeof error === "object" &&
          error !== null &&
          !Array.isArray(error)
        ) {
          // Nested object - recurse
          processErrors(error, fullField);
        } else {
          // String error or array - display it
          const errorMessage = Array.isArray(error)
            ? error.join(", ")
            : String(error);
          errorItems.push(
            <Typography
              component="li"
              variant="body2"
              key={`error-${keyIndex++}`}
            >
              {`${fullField}: ${errorMessage}`}
            </Typography>,
          );
        }
      });
    };

    processErrors(errors);

    return (
      <Collapse in={true}>
        <Alert severity="warning" sx={{ mb: { xs: 1, sm: 2, md: 3 } }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Please fix the following errors:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {errorItems}
          </Box>
        </Alert>
      </Collapse>
    );
  };

  if (isLoading) {
    return (
      <Container maxWidth={maxWidth}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={maxWidth}
      sx={{
        minHeight: "100vh",
        py: { xs: 1, sm: 2, md: 3 },
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth:
            maxWidth === "sm" ? "400px" : maxWidth === "md" ? "600px" : "100%",
        }}
      >
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: { xs: 1, sm: 2, md: 3 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton onClick={handleBack}>
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{ fontWeight: 600 }}
                  >
                    {title}
                  </Typography>
                  {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                      {subtitle}
                    </Typography>
                  )}
                  {completionPercentage !== undefined && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Form completion: {completionPercentage}%
                      </Typography>
                      <Box
                        sx={{
                          width: 100,
                          height: 4,
                          bgcolor: "grey.300",
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${completionPercentage}%`,
                            height: "100%",
                            bgcolor:
                              completionPercentage === 100
                                ? "success.main"
                                : "primary.main",
                            transition: "width 0.3s ease-in-out",
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showKeyboardShortcuts && (
                  <>
                    <Tooltip title="Keyboard shortcuts">
                      <IconButton
                        aria-label="Keyboard shortcuts"
                        size="small"
                        onClick={handleOpenShortcuts}
                      >
                        <KeyboardIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Popover
                      open={isShortcutsPopoverOpen}
                      anchorEl={shortcutsAnchorEl}
                      onClose={handleCloseShortcuts}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <Box sx={{ p: 2, minWidth: 260 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1.5 }}
                        >
                          Keyboard shortcuts
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "auto 1fr",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Ctrl/Cmd + S
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Save changes
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Escape
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Go back
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Tab
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Navigate between fields
                          </Typography>
                        </Box>
                      </Box>
                    </Popover>
                  </>
                )}
                <AddButton
                  type="submit"
                  showIcon={false}
                  disabled={isSubmitting}
                  sx={{
                    ...submitButtonSx,
                  }}
                >
                  {isSubmitting ? "Saving..." : submitButtonText}
                </AddButton>
              </Box>
            </Box>

            {/* Success Message */}
            {successMessage && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: { xs: 1, sm: 2, md: 3 } }}
              >
                {successMessage}
              </Alert>
            )}

            {/* Error Message */}
            {errorMessage && (
              <Alert severity="error" sx={{ mb: { xs: 1, sm: 2, md: 3 } }}>
                {errorMessage}
              </Alert>
            )}

            {/* Form Validation Summary */}
            {renderFormErrors()}

            {/* Form Status Indicator */}
            {showUnsavedChangesWarning && dirty && (
              <Alert severity="info" sx={{ mb: { xs: 1, sm: 2, md: 3 } }}>
                <Typography variant="body2">
                  You have unsaved changes. Don't forget to save your work!
                </Typography>
              </Alert>
            )}

            {/* Form Content */}
            {children}
          </form>
        </FormikProvider>
      </Box>
    </Container>
  );
};

export default BaseFormPage;
